
import { useClients } from "@/hooks/useClients";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ClientStatusReport from "@/components/dashboard/reports/ClientStatusReport";
import { BarChart3, Users, TrendingUp, Clock } from "lucide-react";
import { useMemo } from "react";

export default function ClientStatus() {
  const { data: clients = [], isLoading, error } = useClients();

  const statusStats = useMemo(() => {
    if (!clients.length) return [];

    const statusCounts = clients.reduce((acc, client) => {
      acc[client.status] = (acc[client.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(statusCounts).map(([status, count]) => ({
      name: status,
      value: count
    }));
  }, [clients]);

  const statusLabels: Record<string, string> = {
    'confirmed': 'مؤكد',
    'waiting': 'في الانتظار',
    'in_progress': 'قيد التنفيذ',
    'pricing_requested': 'طلب تسعير',
    'pricing_sent': 'تم إرسال السعر',
    'booking_confirmed': 'تأكيد الحجز',
    'traveling': 'في رحلة',
    'customer_service': 'خدمة العملاء',
    'cancelled': 'ملغي',
    'active': 'نشط',
    'inactive': 'غير نشط',
    'completed': 'مكتمل',
    'waiting_supplier': 'انتظار المورد'
  };

  const getStatusCounts = () => {
    const counts = {
      total: clients.length,
      active: clients.filter(c => ['confirmed', 'in_progress', 'traveling'].includes(c.status)).length,
      pending: clients.filter(c => ['waiting', 'pricing_requested', 'pricing_sent', 'waiting_supplier'].includes(c.status)).length,
      completed: clients.filter(c => ['booking_confirmed'].includes(c.status)).length,
    };
    return counts;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">جاري التحميل...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-red-600">حدث خطأ في تحميل البيانات</div>
      </div>
    );
  }

  const counts = getStatusCounts();

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <div className="flex items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">إدارة الحالات</h1>
      </div>

      {/* بطاقات الإحصائيات */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4" />
              إجمالي العملاء
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counts.total}</div>
            <p className="text-xs opacity-80">جميع العملاء</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              العملاء النشطين
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counts.active}</div>
            <p className="text-xs opacity-80">مؤكد، قيد التنفيذ، في رحلة</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              في الانتظار
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counts.pending}</div>
            <p className="text-xs opacity-80">انتظار، طلب تسعير، تم الإرسال</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              مكتمل
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counts.completed}</div>
            <p className="text-xs opacity-80">العملاء المكتملين</p>
          </CardContent>
        </Card>
      </div>

      {/* التبويبات */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            نظرة عامة
          </TabsTrigger>
          <TabsTrigger value="detailed" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            تفصيلي
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            تحليلات
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <ClientStatusReport data={statusStats} />
        </TabsContent>

        <TabsContent value="detailed" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>التفاصيل حسب الحالة</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {statusStats.map((status) => (
                  <div key={status.name} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">
                      {statusLabels[status.name] || status.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-blue-600">{status.value}</span>
                      <span className="text-sm text-gray-500">
                        ({((status.value / clients.length) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>معدل التحويل</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span>من الانتظار إلى المؤكد:</span>
                    <span className="font-bold text-green-600">
                      {counts.total > 0 ? ((counts.active / counts.total) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>معدل الإكمال:</span>
                    <span className="font-bold text-blue-600">
                      {counts.total > 0 ? ((counts.completed / counts.total) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>الاتجاهات</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-600">
                      {counts.total > 0 ? ((counts.active / counts.total) * 100).toFixed(0) : 0}%
                    </div>
                    <p className="text-sm text-gray-600">نسبة العملاء النشطين</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
